import {
  BellRing,
  CalendarClock,
  Coins,
  CreditCard,
  FileWarning,
  Inbox,
  MonitorOff,
  ShieldCheck,
  Smartphone,
  Unplug,
  type LucideIcon,
} from 'lucide-react';
import type { NotificationPreferences } from '../../api/types';

export type NoticeKind = NotificationPreferences['kinds'][number];

interface Words {
  icon: LucideIcon;
  title: string;
  hint: string;
}

/** Each kind of notice in a person's words: what happened, and what it means for them. */
const words: Partial<Record<NoticeKind['kind'], Words>> = {
  setup_waiting: {
    icon: Inbox,
    title: 'Documents are waiting for a job’s setup',
    hint: 'A program sent documents into a job nobody has set up. Admins only.',
  },
  review_waiting: {
    icon: Inbox,
    title: 'Documents are waiting for review',
    hint: 'One notice a job, when nobody is at its review screen.',
  },
  needs_attention: {
    icon: FileWarning,
    title: 'A document needs a person',
    hint: 'Cortexa could not finish it on its own.',
  },
  breaker_open: {
    icon: Unplug,
    title: 'A destination stopped taking documents',
    hint: 'Paige kept them. Fix the cause and press Retry; nothing is lost.',
  },
  credits_exhausted: {
    icon: Coins,
    title: 'Pages ran out',
    hint: 'Documents wait until pages are back.',
  },
  device: {
    icon: MonitorOff,
    title: 'A scanner or folder went quiet',
    hint: 'A computer stopped sending, or a folder cannot be read.',
  },
  mobile_waiting: {
    icon: Smartphone,
    title: 'Someone is waiting to send from their phone',
    hint: 'A person at your company signed in to Paige Mobile.',
  },
  payment_failed: {
    icon: CreditCard,
    title: 'A card payment did not go through',
    hint: 'Pages for the new month wait until it is paid.',
  },
  plan_billing: {
    icon: CalendarClock,
    title: 'Your plan and invoices',
    hint: 'A yearly renewal coming, an invoice due or past due.',
  },
  security: {
    icon: ShieldCheck,
    title: 'Security',
    hint: 'API keys, connected devices and Ademero support opening your company.',
  },
};

/** A kind this screen has no words for yet is still listed, by its name, never hidden. */
export function noticeWords(kind: NoticeKind['kind']): Words {
  return (
    words[kind] ?? {
      icon: BellRing,
      title: sentenceCase(kind.replace(/_/g, ' ')),
      hint: '',
    }
  );
}

/**
 * Why a locked kind has no off switch: said on its own row, in place of nothing. One true line per
 * kind: a failed card payment is told to the admins only, so "for everyone" was false on its row.
 */
export const lockedHint = (kind: NoticeKind['kind']): string =>
  kind === 'payment_failed' || kind === 'plan_billing'
    ? 'Always on, for every admin.'
    : 'Always on, for everyone.';

export const noticeLines = {
  title: 'Notices',
  bell: 'In Paige',
  email: 'Email',
  reading: 'Reading your notice choices…',
  unread: 'Your notice choices could not be read. Try again in a moment.',
  unsaved: 'That change did not save. Try again in a moment.',
};

function sentenceCase(text: string): string {
  return text ? text[0]!.toUpperCase() + text.slice(1) : text;
}
